import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { changeLoginStatus } from '../../actions/index';
import * as SessionAnalyticsService from 'services/SessionAnalyticsService/SessionAnalyticsService';
import { logout } from '../../api/user';
import { sendTestTicketsToExpert } from '../../api/tickets';
import { changeTab } from '../../actions/creatorsSync';
import { updateUser } from '../../actions';
import { getLoginRoleInfo, syncStoreWithCurrentUserState } from '../../util/loaderUtils';
import {
  SubmitWrapper,
  SmallText,
  ContentWrapper,
  TextInput,
  ResendLinkContainer,
  ResendLink,
  SmallTextBlue,
  ExpirationLinkWrapper,
  ExpirationLink,
} from './MFAPage.styles';
import { Container, LoginBox, Title, Error, Form, Submit } from '../LoginPage/LoginPage.styles';
import { handleError } from 'services/ErrorService/ErrorService';
import AuthLayout from '../AuthLayout/Old/AuthLayout';
import environment from '../../environment';
import AuthService from 'services/authService';
import { getExpertById } from '../../api/expertQueries/expertQueriesGraphQL';
import * as SimplrErrors from '@asurion-core/typescript-utils/SimplrErrors';
import makeRequest from '../../api/makeRequest';
import { ACCOUNT_USER } from '../../constants/roles';

const MFAPage = (props) => {
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const [code, setCode] = useState(new Array(6).fill(''));
  const [error, setError] = useState('');
  const [resendCodeTimer, setResetCodeTimer] = useState(environment.app.mfaResendCodeTimeout);
  const [showTokenExpirationMessage, setShowTokenExpirationMessage] = useState(false);
  const backspaceKeyCode = 8;
  const firstInput = useRef(null);

  useEffect(() => {
    if (!user?.mfaToken) {
      props.history.push('/login');
    }
    if (firstInput.current) {
      firstInput.current.focus();
    }
  }, [props, user]);

  useEffect(() => {
    let timer = null;

    if (resendCodeTimer > 0) {
      timer = setTimeout(() => {
        setResetCodeTimer(resendCodeTimer - 1);
      }, 1000);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [resendCodeTimer]);

  const signIn = async (event) => {
    event.preventDefault();
    try {
      const loginParams = {
        passcode: code.join(''),
        token: user.mfaToken,
      };

      const URL = `${environment.services.authServiceUrl}/v1/user/verifySecondFactor`;
      const { result: responseUser } = await makeRequest(URL, {
        method: 'POST',
        data: loginParams,
      });

      if (!responseUser || !responseUser.sessionToken) {
        throw new Error('Login attempt has failed. Please try again.');
      }

      await AuthService.login(responseUser.sessionToken);

      // 2025-08-07|grigor.minasyan|SPD-12844 we have new account user types who are not experts, they only have access to Logic Builder
      const isAccountUser = AuthService.current().roles?.includes(ACCOUNT_USER);
      if (isAccountUser) {
        window.location.href = '/logic-builder/';
        return;
      }

      const dispatchUpdateUser = (userObj) => dispatch(updateUser(userObj));

      await syncStoreWithCurrentUserState(dispatchUpdateUser);

      // get the expert id
      const simplrExpertId = AuthService.current().simplrExpert?.id;
      const simplrExpert = simplrExpertId ? await getExpertById(simplrExpertId) : null;

      // if expert id exists then they are an expert
      if (!simplrExpert) {
        // otherwise its an SMB
        logout();
      } else {
        if (!simplrExpert.isActive) {
          // account is inactive, don't allow them to login.
          logout();
        } else {
          dispatch(changeLoginStatus(true));

          // specialist portal default tab is All tab
          const { isAdmin, isAdminOrAccountUser } = await getLoginRoleInfo();
          if (isAdminOrAccountUser) {
            dispatch(changeTab('all'));
          } else if (simplrExpert.expertType === 'NEW') {
            // In most cases this will not be called because expert NEW status will be set to TRAINING on signup
            // but if anything goes wrong this ensures that initial login leads to test tickets sent
            await sendTestTicketsToExpert(simplrExpert.id);
          }

          /** NOTE(apoorvyadav1|SPD-13362|2025-10-15):
           * Previously, FullStory was initialized when the website loaded, which is not ideal for privacy compliance.
           * Initialize FullStory if user is logged in and it's not already initialized
           */
          SessionAnalyticsService.initializeSessionAnalyticsServices();

          SessionAnalyticsService.identify(simplrExpert.id, {
            name: `${simplrExpert.firstName} ${simplrExpert.lastName}`,
            email: simplrExpert.email,
            specialist: isAdmin,
          });

          SessionAnalyticsService.event('User Logged In');
        }
      }
    } catch (err) {
      if (err.response?.code === SimplrErrors.Codes.TOKEN_EXPIRED) {
        setShowTokenExpirationMessage(true);
      } else {
        let errorMessage = 'Code Verification failed';

        if (typeof err.message === 'string') {
          errorMessage = err.message;
        } else if (err.message?.message) {
          errorMessage = err.message.message;
        }

        setError(errorMessage);
      }

      handleError({ error: err });
    }
  };

  const changeOtpInputFocusOnKeyDown = (target, keyPressedCode) => {
    const { value, previousSibling, nextSibling } = target;
    const isBackspace = keyPressedCode === backspaceKeyCode;
    const isBlankCurrentValue = value === '';
    if (isBackspace && isBlankCurrentValue && previousSibling) {
      previousSibling.focus();
    } else if (!isBackspace && !isBlankCurrentValue && nextSibling) {
      nextSibling.focus();
    } else if (value.length === 1) {
      target.value = value.charAt(0);
    }
  };

  const handleInputChange = (e, index) => {
    e.preventDefault();
    const codeArray = [...code];
    codeArray[index] = e.target.value.slice(0, 1);
    setCode(codeArray);
    setError('');
  };

  const handleResendCode = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const loginParams = {
        token: user.mfaToken,
      };

      const URL = `${environment.services.authServiceUrl}/v1/user/resendVerification`;
      await makeRequest(URL, {
        method: 'POST',
        data: loginParams,
      });

      handleCountDown();
    } catch (err) {
      let errorMessage = 'Resend Code failed';

      if (typeof err?.message === 'string') {
        errorMessage = err.message;
      } else if (typeof err?.message?.message === 'string') {
        errorMessage = err.message.message;
      }

      setError(errorMessage);

      handleError({ error: err });
    }
  };

  const handleCountDown = () => {
    setResetCodeTimer(environment.app.mfaResendCodeTimeout);
    setCode(new Array(6).fill(''));
  };

  return (
    <AuthLayout>
      <Container>
        <LoginBox>
          {showTokenExpirationMessage ? (
            <ContentWrapper>
              <Title>Code expired</Title>
              <SmallText>Sorry but codes expire 5 mins after you request it.</SmallText>
              <ExpirationLinkWrapper>
                <ExpirationLink href="/login">Sign in</ExpirationLink>
              </ExpirationLinkWrapper>
            </ContentWrapper>
          ) : (
            <Form name="signIn" action="expert" onSubmit={(e) => signIn(e)}>
              <ContentWrapper>
                <Title>Verify it's you</Title>
                <SmallText>
                  {`We sent a code to the phone number registered in our records that ends with the digits ${user?.phoneNumberLastDigits}`}
                </SmallText>
                <ResendLinkContainer>
                  {resendCodeTimer ? (
                    <SmallText>
                      Resend Code <SmallTextBlue>in {resendCodeTimer}s</SmallTextBlue>
                    </SmallText>
                  ) : (
                    <ResendLink to="#" onClick={(e) => handleResendCode(e)}>
                      Resend Code
                    </ResendLink>
                  )}
                </ResendLinkContainer>
                <fieldset className="number-code">
                  {code.map((v, index) => (
                    <TextInput
                      key={index}
                      value={v}
                      onKeyDown={(e) => changeOtpInputFocusOnKeyDown(e.target, e.keyCode)}
                      onChange={(e) => handleInputChange(e, index)}
                      autoComplete="off"
                      name="code"
                      type="text"
                      className={error ? 'error' : ''}
                      data-testid={`codeInput_${index}`}
                      ref={index === 0 ? firstInput : null}
                    />
                  ))}
                </fieldset>
                {!!error && <Error>{error}</Error>}
                <SubmitWrapper className={error ? 'error' : ''}>
                  <Submit
                    type="submit"
                    value="Sign in"
                    disabled={code.filter((item) => item.length === 0).length || error}
                  >
                    Sign in
                  </Submit>
                </SubmitWrapper>
              </ContentWrapper>
            </Form>
          )}
        </LoginBox>
      </Container>
    </AuthLayout>
  );
};

export default withRouter(MFAPage);
